// A Firestore server in memory, for the unit tests and the demo: documents by path, a server clock, listeners that
// hear every commit, and hooks to go offline, refuse a batch, or fail a listener. The real sync runs on it unchanged.
//
// It answers like Firestore without its cache: every snapshot comes from the server and holds no pending writes.
// Offline, commits wait (their promises stay open) and are applied in order once it is back online, as Firestore's
// queue does.

import { DELETE_FIELD, SERVER_TIMESTAMP } from "../core/changes";
import { isPlainObject, momentOf, sameValue, type Fields } from "../core/records";
import type {
  Remote, RemoteDocument, RemoteDocumentSnapshot, RemoteError, RemoteQuery, RemoteSnapshot, RemoteWrite, StoredValueCodec,
} from "./remote";

/** Just enough of Firestore's Timestamp: seconds and nanoseconds, `toDate`, `isEqual`. */
export class MemoryTimestamp {
  constructor(readonly seconds: number, readonly nanoseconds: number) {}

  static fromDate(date: Date): MemoryTimestamp {
    const ms = date.getTime();
    const seconds = Math.floor(ms / 1000);
    return new MemoryTimestamp(seconds, (ms - seconds * 1000) * 1_000_000);
  }

  toDate(): Date {
    return new Date(this.seconds * 1000 + Math.floor(this.nanoseconds / 1_000_000));
  }

  toMillis(): number {
    return this.toDate().getTime();
  }

  isEqual(other: unknown): boolean {
    return other instanceof MemoryTimestamp && other.seconds === this.seconds && other.nanoseconds === this.nanoseconds;
  }
}

interface Listener {
  query?: RemoteQuery;
  path?: string;
  next: (snapshot: never) => void;
  failed: (error: RemoteError) => void;
  /** The ids the query held at its last snapshot. */
  held: Set<string>;
  active: boolean;
}

interface Waiting {
  writes: readonly RemoteWrite[];
  resolve: () => void;
  reject: (error: unknown) => void;
}

export class MemoryRemote implements Remote {
  /** Documents by path. */
  readonly documents = new Map<string, Fields>();
  /** Every committed batch, in order, with its writes as given (sentinels and all): what the tests look at. */
  readonly commits: RemoteWrite[][] = [];
  /** Answers each commit with an error to refuse it, or null to take it. */
  refuse: ((writes: readonly RemoteWrite[]) => RemoteError | null) | null = null;
  private clock: number;
  private online = true;
  private readonly waiting: Waiting[] = [];
  private readonly listeners = new Set<Listener>();
  /** How many listeners were set up, per path: tests see when a listener started again. */
  readonly listenCounts = new Map<string, number>();

  constructor(start: Date = new Date("2026-10-11T08:00:00Z")) {
    this.clock = start.getTime();
  }

  /** The server's time now; every write moves it on by a millisecond, so `modified` always grows. */
  now(): Date {
    return new Date(this.clock);
  }

  advance(ms: number): void {
    this.clock += ms;
  }

  // MARK: Remote

  listen(query: RemoteQuery, next: (snapshot: RemoteSnapshot) => void, failed: (error: RemoteError) => void): () => void {
    this.listenCounts.set(query.path, (this.listenCounts.get(query.path) ?? 0) + 1);
    const listener: Listener = { query, next: next as (snapshot: never) => void, failed, held: new Set(), active: true };
    this.listeners.add(listener);
    queueMicrotask(() => this.notify(listener, null));
    return () => this.detach(listener);
  }

  listenToDocument(path: string, next: (snapshot: RemoteDocumentSnapshot) => void, failed: (error: RemoteError) => void): () => void {
    this.listenCounts.set(path, (this.listenCounts.get(path) ?? 0) + 1);
    const listener: Listener = { path, next: next as (snapshot: never) => void, failed, held: new Set(), active: true };
    this.listeners.add(listener);
    queueMicrotask(() => this.notify(listener, null));
    return () => this.detach(listener);
  }

  commit(writes: readonly RemoteWrite[]): Promise<void> {
    return new Promise((resolve, reject) => {
      this.waiting.push({ writes, resolve, reject });
      if (this.online) this.flush();
    });
  }

  async readFromServer(path: string): Promise<Fields | null> {
    if (!this.online) throw Object.assign(new Error("offline"), { code: "unavailable" });
    const data = this.documents.get(path);
    return data === undefined ? null : clone(data);
  }

  toStoredMoment(date: Date): unknown {
    return MemoryTimestamp.fromDate(date);
  }

  readonly values: StoredValueCodec = {
    freeze: (value) => (value instanceof MemoryTimestamp ? { tag: "timestamp", value: [value.seconds, value.nanoseconds] } : undefined),
    thaw: (tag, value) => {
      if (tag !== "timestamp") return value;
      const [seconds, nanoseconds] = value as [number, number];
      return new MemoryTimestamp(seconds, nanoseconds);
    },
  };

  // MARK: Test hooks

  /** Offline, commits wait; back online, they are applied in order. */
  setOnline(online: boolean): void {
    this.online = online;
    if (online) this.flush();
  }

  /** Commits still waiting for the connection. */
  get waitingCommits(): number {
    return this.waiting.length;
  }

  /**
   * Puts a document on the server as another device would: Dates become stored moments, and `modified` is set by
   * the server unless the data has one (pass `modified: null` to leave it out). Listeners hear it.
   */
  put(path: string, data: Fields): void {
    const stored = this.stored(data) as Fields;
    if (!("modified" in data)) stored.modified = this.tick();
    else if (data.modified === null) delete stored.modified;
    this.documents.set(path, stored);
    this.changed([path]);
  }

  /** Changes fields of a document as another device would (merged by field), with a new `modified`. */
  update(path: string, fields: Fields): void {
    const current = this.documents.get(path) ?? {};
    const next: Fields = { ...current };
    for (const [key, value] of Object.entries(fields)) {
      if (value === DELETE_FIELD) delete next[key];
      else next[key] = this.stored(value);
    }
    next.modified = this.tick();
    this.documents.set(path, next);
    this.changed([path]);
  }

  /** A tombstone, as another device leaves one. */
  tombstone(path: string): void {
    this.documents.set(path, { deleted: true, modified: this.tick() });
    this.changed([path]);
  }

  /** Removes a document without a trace (the server's `share` function ending a share does this to `shares/{id}`). */
  remove(path: string): void {
    this.documents.delete(path);
    this.changed([path]);
  }

  private detach(listener: Listener): void {
    listener.active = false;
    this.listeners.delete(listener);
  }

  /** Every listener on a path (a collection's or a document's) reports the error and stops, as Firestore's do. */
  failListeners(path: string, error: RemoteError): void {
    for (const listener of [...this.listeners]) {
      if ((listener.query?.path ?? listener.path) !== path) continue;
      this.detach(listener);
      listener.failed(error);
    }
  }

  /** The documents of a collection (not its subcollections), by id. */
  collection(path: string): Map<string, Fields> {
    const result = new Map<string, Fields>();
    for (const [key, data] of this.documents) {
      if (parentOf(key) === path) result.set(idOf(key), data);
    }
    return result;
  }

  get activeListeners(): number {
    return this.listeners.size;
  }

  // MARK: Inside

  private tick(): MemoryTimestamp {
    this.clock += 1;
    return MemoryTimestamp.fromDate(new Date(this.clock));
  }

  /** Dates as stored moments, the way the Firestore library stores them. */
  private stored(value: unknown): unknown {
    if (value instanceof Date) return MemoryTimestamp.fromDate(value);
    if (Array.isArray(value)) return value.map((item) => this.stored(item));
    if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([key, inner]) => [key, this.stored(inner)]));
    return value;
  }

  private flush(): void {
    while (this.online && this.waiting.length > 0) {
      const { writes, resolve, reject } = this.waiting.shift()!;
      const refusal = this.refuse?.(writes) ?? null;
      if (refusal) {
        reject(Object.assign(new Error(refusal.message), { code: refusal.code }));
        continue;
      }
      const stamp = this.tick();
      const resolveValue = (value: unknown): unknown => {
        if (value === SERVER_TIMESTAMP) return stamp;
        if (Array.isArray(value)) return value.map(resolveValue);
        if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([key, inner]) => [key, resolveValue(inner)]));
        return this.stored(value);
      };
      for (const { path, write } of writes) {
        if (write.kind === "set") {
          const fields: Fields = {};
          for (const [key, value] of Object.entries(write.fields)) if (value !== DELETE_FIELD) fields[key] = resolveValue(value);
          this.documents.set(path, fields);
        } else {
          const fields: Fields = { ...(this.documents.get(path) ?? {}) };
          for (const [key, value] of Object.entries(write.fields)) {
            if (value === DELETE_FIELD) delete fields[key];
            else fields[key] = resolveValue(value);
          }
          this.documents.set(path, fields);
        }
      }
      this.commits.push([...writes]);
      this.changed(writes.map((write) => write.path));
      resolve();
    }
  }

  private changed(paths: readonly string[]): void {
    for (const listener of [...this.listeners]) this.notify(listener, new Set(paths));
  }

  private notify(listener: Listener, paths: Set<string> | null): void {
    if (!listener.active) return;
    if (listener.path !== undefined) {
      if (paths && !paths.has(listener.path)) return;
      const data = this.documents.get(listener.path);
      (listener.next as (snapshot: RemoteDocumentSnapshot) => void)({
        data: data === undefined ? null : clone(data), fromCache: false, hasPendingWrites: false,
      });
      return;
    }
    const query = listener.query!;
    const matching = new Map<string, Fields>();
    for (const [id, data] of this.collection(query.path)) if (matches(data, query)) matching.set(id, data);
    const changes: RemoteSnapshot["changes"] = [];
    const touched = (id: string) => paths === null || paths.has(`${query.path}/${id}`);
    for (const [id, data] of matching) {
      if (!touched(id)) continue;
      changes.push({ type: listener.held.has(id) ? "modified" : "added", doc: { id, data: clone(data) } });
    }
    for (const id of listener.held) if (!matching.has(id) && touched(id)) changes.push({ type: "removed", doc: { id, data: {} } });
    // A listener hears its first snapshot even when empty, later ones only when something it holds changed.
    if (paths !== null && changes.length === 0) return;
    listener.held = new Set(matching.keys());
    const docs: RemoteDocument[] = [...matching].map(([id, data]) => ({ id, data: clone(data) }));
    (listener.next as (snapshot: RemoteSnapshot) => void)({ changes, docs, fromCache: false, hasPendingWrites: false });
  }
}

function matches(data: Fields, query: RemoteQuery): boolean {
  if (query.modifiedAfter) {
    const modified = momentOf(data.modified);
    if (!modified || modified.getTime() <= query.modifiedAfter.getTime()) return false;
  }
  if (query.where) {
    const value = data[query.where.field];
    if (query.where.op === "==") return sameValue(value, query.where.value);
    return Array.isArray(value) && value.some((item) => sameValue(item, query.where!.value));
  }
  return true;
}

function parentOf(path: string): string {
  return path.slice(0, path.lastIndexOf("/"));
}

function idOf(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** A copy of stored data, so nobody changes the server's documents by holding them. Timestamps are immutable. */
function clone(value: Fields): Fields {
  const copy = (inner: unknown): unknown => {
    if (Array.isArray(inner)) return inner.map(copy);
    if (isPlainObject(inner)) return Object.fromEntries(Object.entries(inner).map(([key, item]) => [key, copy(item)]));
    return inner;
  };
  return copy(value) as Fields;
}
